import React, { useEffect, useState } from "react";
import { useLoaderData, Await, Outlet } from "react-router-dom";
import AuthProvider from "./context/authContext";
import ErrorBoundary from "@src/components/utility/ErrorBoundary";
import { Loader } from "@src/components/atoms";
import RootAppLayout from "@src/components/layout/RootAppLayout";
import ToastProvider from "@src/context/toastContext";
import LoaderFullPage from "./components/atoms/LoaderFullPage";
import { useCheckIdentityId } from "./utils/checkIdentityId";
import {
  ROOT_LOADING_MESSAGES,
  ROOT_LOADING_MESSAGE_INTERVAL_MS,
  ROOT_LOADING_SHOW_DELAY_MS,
  ROOT_LOADING_TIMEOUT_MS,
} from "@src/constants/constants";

// Defined at module scope (not inside App) so its show-delay/timeout timers survive
// any re-render of App while auth is still resolving.
const RootSkeleton = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(
      () => setShowLoader(true),
      ROOT_LOADING_SHOW_DELAY_MS,
    );
    const timeoutTimer = setTimeout(
      () => setTimedOut(true),
      ROOT_LOADING_TIMEOUT_MS,
    );
    return () => {
      clearTimeout(showTimer);
      clearTimeout(timeoutTimer);
    };
  }, []);

  // Caught by the <ErrorBoundary> this renders under, showing the page-wide error state.
  if (timedOut) {
    throw new Error("Timed out waiting for the app to load.");
  }

  if (!showLoader) return null;

  return (
    <LoaderFullPage
      messages={ROOT_LOADING_MESSAGES}
      intervalMs={ROOT_LOADING_MESSAGE_INTERVAL_MS}
    />
  );
};

export default function App() {
  const { authSession } = useLoaderData(); // initial data loaded during navigation (before root render occurs)
  useCheckIdentityId();

  return (
    <ErrorBoundary>
      <React.Suspense fallback={<RootSkeleton />}>
        <Await resolve={authSession}>
          {(authSession) => (
            <AuthProvider currentUser={authSession}>
              <ToastProvider>
                <Outlet />
              </ToastProvider>
            </AuthProvider>
          )}
        </Await>
      </React.Suspense>
    </ErrorBoundary>
  );
}
